import React, { useMemo, useState } from "react";
import { FaCogs, FaUndo } from "react-icons/fa";
import useAuth from "../../Hooks/useAuth";
import { useGetPaymentOperationLogsPaginatedQuery } from "../../store";
import { matchMachineRecords, toMachineRecordRow } from "../../utils/machineRecord";
import { formatDate, formatTL } from "./walletTxUi";

const STEP_PREVIEW = 4;

const itemsText = (items) =>
  items
    .map((it) => {
      const name = it.productName || it.name || "Ürün";
      return it.payBack ? `${name} (iade)` : name;
    })
    .join(", ");

function RecordCard({ row }) {
  const [showAllSteps, setShowAllSteps] = useState(false);
  const steps = showAllSteps ? row.steps : row.steps.slice(0, STEP_PREVIEW);
  return (
    <div className="bg-gray-50 rounded-xl p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-SemiBold text-gray-800 truncate">
            {row.deviceName || "Makine"} · <span className="font-mono">{row.serialNo || "-"}</span>
          </div>
          <div className="text-xs text-gray-500">
            {formatDate(row.date)} · {row.methodLabel}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-SemiBold text-gray-800">{formatTL(row.amountTL)}</div>
          {row.refund && (
            <span className="inline-flex items-center gap-1 text-[11px] font-SemiBold text-orange-700">
              <FaUndo className="w-2.5 h-2.5" /> İade işaretli
            </span>
          )}
        </div>
      </div>
      {row.items.length > 0 && (
        <p className="text-xs text-gray-600 break-words">{itemsText(row.items)}</p>
      )}
      {row.steps.length > 0 && (
        <div>
          <ol className="relative border-l border-gray-200 ml-1.5 space-y-1.5">
            {steps.map((step, i) => (
              <li key={i} className="ml-3">
                <span className="absolute -left-[4px] w-2 h-2 rounded-full bg-primary" />
                <span className="text-xs text-gray-700">{step.message || step.type || "-"}</span>
                <span className="text-[11px] text-gray-400"> · {formatDate(step.createdInfo)}</span>
              </li>
            ))}
          </ol>
          {row.steps.length > STEP_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAllSteps((v) => !v)}
              className="mt-1 text-[11px] font-Medium text-indigo-600 hover:text-indigo-700"
            >
              {showAllSteps ? "Daha az göster" : `Tüm adımlar (${row.steps.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Satış/İade detayında makinenin kendi kaydı (#243) — makine, ürünler, adımlar.
 * Fatura (PaymentBill) ile makine logu (DeviceOperationInfoLog) Intent ID /
 * Fatura No üzerinden eşleşir; karşılığı olmayabilir (ör. makine log
 * göndermediyse). İade faturası, iade işaretli aynı satış kaydına eşleşir.
 */
export default function MachineRecordSection({ intentId, reference }) {
  const { auth } = useAuth();
  const key = intentId || reference;
  const { data, isFetching, isError } = useGetPaymentOperationLogsPaginatedQuery(
    { token: auth?.accessToken, page: 0, limit: 10, search: key },
    { skip: !key || !auth?.accessToken }
  );
  const records = useMemo(
    () => matchMachineRecords((data?.data || []).map(toMachineRecordRow), { intentId, reference }),
    [data, intentId, reference]
  );

  if (!key) return null;
  return (
    <div>
      <h4 className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
        <FaCogs className="w-3 h-3" /> Makine Kaydı
      </h4>
      {isFetching ? (
        <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />
      ) : isError ? (
        <p className="text-xs text-gray-500">Makine kaydı yüklenemedi.</p>
      ) : records.length === 0 ? (
        <p className="text-xs text-gray-500">Bu işlem için makineden gelen kayıt yok.</p>
      ) : (
        <div className="space-y-2">
          {records.map((row) => (
            <RecordCard key={row._id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}
